import React from 'react'

function fmtVal(value, decimals) {
  if (value == null) return null
  if (decimals === 0) return String(Math.round(value))
  return value.toFixed(decimals ?? 1)
}

function fmtDiff(diff, normalMode, decimals) {
  if (normalMode === 'pct') return `${Math.round(diff)} %`
  const sign = diff > 0 ? '+' : ''
  return `${sign}${diff.toFixed(decimals ?? 1)}`
}

function diffCls(diff, normalMode) {
  const d = normalMode === 'pct' ? diff - 100 : diff
  if (d > 0.05) return 'text-red-600 font-medium'
  if (d < -0.05) return 'text-blue-600 font-medium'
  return 'text-gray-400'
}

function makeTip(valStr, normalStr, diff, normalMode, unit) {
  if (normalMode === 'pct') {
    return `${valStr} / normál ${normalStr} × 100 = ${Math.round(diff)} %`
  }
  const sign = diff > 0 ? '+' : ''
  return `${valStr} − normál ${normalStr} = ${sign}${diff.toFixed(1)} ${unit ?? ''}`
}

function diffOf(value, normal, normalMode) {
  return normalMode === 'pct' ? (value / normal * 100) : (value - normal)
}

const cb = 'border-b border-r border-gray-200 px-1 py-0.5 text-xs tabular-nums whitespace-nowrap'
const thBase = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1 py-1 text-xs font-normal text-center bg-gray-50'
const thBase2 = `${thBase} border-b-gray-400`

export function YlyStatsGrid({ rowGroups, rowGroupsTransposed, orientation = 'normal', dataMap, normalsMap, years }) {
  if (orientation === 'transposed') {
    return (
      <YlyStatsGridTransposed
        rowGroups={rowGroupsTransposed ?? rowGroups}
        dataMap={dataMap}
        normalsMap={normalsMap}
        years={years}
      />
    )
  }

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            {/* Row 1: years */}
            <tr>
              <th
                rowSpan={2}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[16rem] whitespace-nowrap"
              >
                Veličina
              </th>
              {years.map((year) => (
                <th
                  key={year}
                  colSpan={2}
                  className={`${thBase} border-t-2 border-t-gray-400 text-gray-500`}
                >
                  {year}
                </th>
              ))}
            </tr>
            {/* Row 2: hod/odch sub-headers */}
            <tr>
              {years.flatMap((year) => [
                <th key={`${year}-h`} className={`${thBase2} text-gray-400`}>hod.</th>,
                <th key={`${year}-d`} className={`${thBase2} text-gray-300`}>odch.</th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {rowGroups.map((group, gi) => (
              <React.Fragment key={`g${gi}`}>
                <tr>
                  <td className="sticky left-0 z-10 bg-gray-100 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {group.label}
                  </td>
                  {years.flatMap((year) => [
                    <td key={`${year}-v`} className={`${cb} bg-gray-100`} />,
                    <td key={`${year}-d`} className={`${cb} bg-gray-100`} />,
                  ])}
                </tr>
                {group.rows.map(row => (
                  <tr key={row.tsId} className="group hover:bg-slate-50">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap">
                      {row.label}
                      {row.unit && (
                        <span className="text-gray-400 ml-1.5 text-[10px]">({row.unit})</span>
                      )}
                    </td>
                    {years.flatMap((year) => {
                      const value = dataMap?.[row.tsId]?.[year] ?? null
                      const normal = row.normalMode ? (normalsMap?.[row.tsId] ?? null) : null
                      const fv = fmtVal(value, row.decimals)

                      if (row.normalMode && value != null && normal != null) {
                        const diff = diffOf(value, normal, row.normalMode)
                        const fn = fmtVal(normal, row.decimals)
                        const tip = makeTip(fv, fn, diff, row.normalMode, row.unit)
                        return [
                          <td key={`${year}-v`} className={`${cb} text-right bg-white group-hover:bg-slate-50`}>
                            {fv}
                          </td>,
                          <td key={`${year}-d`} title={tip} className={`${cb} text-right bg-white group-hover:bg-slate-50 ${diffCls(diff, row.normalMode)}`}>
                            {fmtDiff(diff, row.normalMode, row.decimals)}
                          </td>,
                        ]
                      }
                      return [
                        <td
                          key={`${year}-v`}
                          className={`${cb} text-right bg-white group-hover:bg-slate-50 ${value == null ? 'text-gray-300' : 'text-gray-700'}`}
                        >
                          {fv ?? '—'}
                        </td>,
                        <td key={`${year}-d`} className={`${cb} bg-gray-50`} />,
                      ]
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Transponovaná varianta ─────────────────────────────────────────────────────
// řádky = roky, sloupce = veličiny (skupina = 1. úroveň hlavičky, veličina + jednotka = 2.,
// hod./odch. = 3.).
function YlyStatsGridTransposed({ rowGroups, dataMap, normalsMap, years }) {
  const flatRows = rowGroups.flatMap(g => g.rows.map(r => ({ ...r, group: g.label })))

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                rowSpan={3}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-600 font-semibold whitespace-nowrap"
              >
                Rok
              </th>
              {rowGroups.map((g, gi) => (
                <th
                  key={g.label}
                  colSpan={g.rows.length * 2}
                  className={`${thBase} border-t-2 border-t-gray-400 text-gray-600 font-semibold ${gi > 0 ? 'border-l border-l-gray-400' : ''}`}
                >
                  {g.label}
                </th>
              ))}
            </tr>
            <tr>
              {rowGroups.flatMap((g, gi) =>
                g.rows.map((row, ri) => (
                  <th
                    key={row.tsId}
                    colSpan={2}
                    className={`${thBase2} text-gray-500 ${gi > 0 && ri === 0 ? 'border-l border-l-gray-400' : ''}`}
                  >
                    {row.label}
                    {row.unit && <span className="text-gray-400 ml-1 text-[10px]">({row.unit})</span>}
                  </th>
                ))
              )}
            </tr>
            <tr>
              {rowGroups.flatMap((g, gi) =>
                g.rows.flatMap((row, ri) => [
                  <th key={`${row.tsId}-h`} className={`${thBase2} text-gray-400 ${gi > 0 && ri === 0 ? 'border-l border-l-gray-400' : ''}`}>hod.</th>,
                  <th key={`${row.tsId}-d`} className={`${thBase2} text-gray-300`}>odch.</th>,
                ])
              )}
            </tr>
          </thead>
          <tbody>
            {years.map((year) => (
              <tr key={year} className="group hover:bg-slate-50">
                <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 group-last:border-b-0 px-3 py-0.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                  {year}
                </td>
                {flatRows.flatMap((row, ri) => {
                  const firstOfGroup = ri > 0 && flatRows[ri - 1].group !== row.group
                  const bl = firstOfGroup ? 'border-l border-l-gray-400' : ''
                  const value = dataMap?.[row.tsId]?.[year] ?? null
                  const normal = row.normalMode ? (normalsMap?.[row.tsId] ?? null) : null
                  const fv = fmtVal(value, row.decimals)

                  if (row.normalMode && value != null && normal != null) {
                    const diff = diffOf(value, normal, row.normalMode)
                    const fn = fmtVal(normal, row.decimals)
                    const tip = makeTip(fv, fn, diff, row.normalMode, row.unit)
                    return [
                      <td key={`${row.tsId}-v`} className={`${cb} text-right bg-white group-hover:bg-slate-50 group-last:border-b-0 ${bl}`}>
                        {fv}
                      </td>,
                      <td key={`${row.tsId}-d`} title={tip} className={`${cb} text-right bg-white group-hover:bg-slate-50 group-last:border-b-0 ${diffCls(diff, row.normalMode)}`}>
                        {fmtDiff(diff, row.normalMode, row.decimals)}
                      </td>,
                    ]
                  }
                  return [
                    <td
                      key={`${row.tsId}-v`}
                      className={`${cb} text-right bg-white group-hover:bg-slate-50 group-last:border-b-0 ${bl} ${value == null ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      {fv ?? '—'}
                    </td>,
                    <td key={`${row.tsId}-d`} className={`${cb} bg-gray-50 group-last:border-b-0`} />,
                  ]
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
